import styled from '@emotion/styled';
import {useBreakpointDown} from '@travel-frontend/core';
import {Block, useExitIntent} from '@travel-frontend/ui-components';
import {useCallback, useEffect, useRef} from 'react';

import {LayerPortal} from '@/components/LayerPortal';
import {PAGE_HOTELS} from '@/consts';
import {useConsent} from '@/modules/common/context/Consent';
import {useHoneepot} from '@/modules/common/context/Honeepot';
import {TravelTypes} from '@/modules/common/context/QueryParams/models';
import {useLocalSplit} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {useTracking} from '@/modules/common/hooks/useTracking';

const WrapperElement = styled(Block)`
  position: fixed;
  inset: 0;
  z-index: 2;
  /* Creates a new stacking context / containing block.
   This is important so that we can tweak the widget position. */
  transform: scale(1);
  pointer-events: none;

  > * {
    pointer-events: all;
  }
`;

export const Honeepot: React.FunctionComponent = () => {
  const honeepot = useHoneepot();
  const [{travelType}] = useSearchParams();
  const split = useLocalSplit<'default' | 'off' | 'on' | 'honeepot'>(
    'CAP_280_split_exit_layer'
  );
  const {dispatchGenericTrackingEvent} = useTracking();
  const mdDown = useBreakpointDown('md');
  const currentPage = useCurrentPage();
  const consent = useConsent();
  const wrapperElement = useRef<HTMLDivElement | null>(null);
  const enabled =
    consent.status?.personalization === true &&
    split.treatment === 'honeepot' &&
    travelType === TravelTypes.packageTour;

  useEffect(
    function init() {
      if (enabled && wrapperElement.current) {
        honeepot.load({
          wrapperElement: wrapperElement.current,
        });
        if (honeepot.api) {
          honeepot.api.showFloatingButton();
        }
      } else if (honeepot.api) {
        honeepot.api.hideFloatingButton();
      }
    },
    [enabled, honeepot]
  );

  useEffect(
    function clickTracking() {
      if (enabled) {
        honeepot.on('click.honeepot-floating-button', () => {
          dispatchGenericTrackingEvent({
            event: 'chatbot',
            event_action: 'honeepot_click',
            non_interaction: false,
          });
        });
      }
      return () => {
        honeepot.off('click.honeepot-floating-button');
      };
    },
    [enabled, honeepot, dispatchGenericTrackingEvent]
  );

  useExitIntent({
    enabled,
    optOutCookieName: 'exit-layer-opt-out',
    optOutCookieExpiryDays: 1,
    onExitIntent: useCallback(() => {
      if (honeepot.api) {
        honeepot.api.show();
        dispatchGenericTrackingEvent({
          event: 'chatbot',
          event_action: 'honeepot_exit_intent',
          non_interaction: true,
        });
      }
    }, [honeepot.api, dispatchGenericTrackingEvent]),
  });

  const bottomSpacing =
    currentPage === PAGE_HOTELS && mdDown && !honeepot.modalVisible
      ? 40
      : undefined;

  return (
    <LayerPortal>
      <WrapperElement ref={wrapperElement} bottom={bottomSpacing} />
    </LayerPortal>
  );
};
